import {
  array,
  boolean,
  forward,
  type InferOutput,
  nullable,
  number,
  object,
  partialCheck,
  picklist,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { nullableDateToStringSchema } from '@/libs/valibot'

import { buildToursChargeAdditionalChargeFormValueSchema } from './tours-charge-additional-charges-form-value'

// 端数処理 (roundType)。付帯費用 (shipment-order-additional-form-value) の RoundTypePicklist と同一定義。
const RoundTypePicklist = picklist(['ROUND_UP', 'ROUND_DOWN', 'ROUND_HALF_UP'])

/**
 * 運行実績の請求額のフォームスキーマを生成する。
 *
 * VOC_PJ-941 #3984: 基本請求額・付帯行の税区分・端数処理の必須チェックは
 * `requireTaxSetting` で制御する（定義は 1 箇所、判定だけ生成時の値を参照）。
 * - 詳細編集の通常時: true（ブランクにした時点でフィールド脇に必須エラー）
 * - キャンセル編集: false（金額 0 初期化で送るため必須にしない = BE ガードの isCanceling 除外と対応）
 * - 一括編集: false（UI に税設定欄がないため。BE ガードが最終防波堤）
 */
export const buildToursChargeCustomerChargeFormValueSchema = ({
  requireTaxSetting
}: {
  requireTaxSetting: boolean
}) =>
  pipe(
    object({
      /** 請求ID */
      customerChargeId: nullable(number()),
      /** 基本請求額（未入力可。VOC_PJ-941 #3984 で払い側・付帯と対称に nullable 化） */
      billedPrice: nullable(number()),
      /**
       * 基本請求額(billedPrice)の税区分マスタ ID（#3328 VOC_PJ-761、案件個別オーバーライド）。
       * `null` のときは BE 側でカスケード fallback（荷主 default → 課税10%）。
       */
      taxCategoryId: nullable(number()),
      /**
       * 基本請求額(billedPrice)の内税フラグ（VOC_PJ-844 #3624、案件個別オーバーライド）。
       * `=== true` のときのみ内税。`null` のときは BE/月次カスケード fallback（取引先 default → 外税）。
       */
      isTaxInclusive: nullable(boolean()),
      /**
       * 基本請求額(billedPrice)の端数処理（VOC_PJ-888 #3917、案件個別オーバーライド）。
       * `null` のときは BE/マスタカスケード fallback（荷主 > 拠点）。
       */
      roundType: nullable(RoundTypePicklist),
      /** その他請求額 */
      otherBilledPrice: number(),
      /**
       * その他請求額(otherBilledPrice)の税区分マスタ ID（#3328 VOC_PJ-761、運行実績側のみ）。
       * `null` のときは BE 側でカスケード fallback。
       */
      otherBilledTaxCategoryId: nullable(number()),
      /** 追加請求料金（税設定必須チェックの有無は本スキーマと連動） */
      additionalCharges: array(
        buildToursChargeAdditionalChargeFormValueSchema({ requireTaxSetting })
      ),
      /** 請求契約ID */
      customerContractId: nullable(number()),
      /** 車種ID */
      truckModelId: nullable(number()),
      /** 総重量 */
      totalWeight: nullable(number()),
      /** 請求計上日 (VOC_PJ-55 T-3、SO 単位、CustomerCharge.operatingDate) */
      operatingDate: nullableDateToStringSchema,
      // カスタムキー1〜5（VOC_PJ-960 #4487）。実績編集での書き換え値（null=未設定）
      customKey1: nullable(string()),
      customKey2: nullable(string()),
      customKey3: nullable(string()),
      customKey4: nullable(string()),
      customKey5: nullable(string())
    }),
    // VOC_PJ-941 #3984 → #4556: 基本請求額の税区分・端数処理は金額の有無に関わらず必須
    // （払い側と対称）。内税フラグは対象外。requireTaxSetting: false（一括編集等）のみ除外。
    forward(
      partialCheck(
        [['taxCategoryId']],
        ({ taxCategoryId }) => !requireTaxSetting || taxCategoryId != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['taxCategoryId']
    ),
    forward(
      partialCheck(
        [['roundType']],
        ({ roundType }) => !requireTaxSetting || roundType != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['roundType']
    )
  )

/** 運行実績の請求額のフォームスキーマ（税設定の必須チェックなし版。一括編集で使用） */
export const ToursChargeCustomerChargeFormValueSchema =
  buildToursChargeCustomerChargeFormValueSchema({ requireTaxSetting: false })

/** 運行実績の請求額のフォーム値 (operatingDate は Date | null を扱う) */
export type ToursChargeCustomerChargeFormValue = Omit<
  InferOutput<typeof ToursChargeCustomerChargeFormValueSchema>,
  'operatingDate'
> & {
  operatingDate: Date | null
}

/** 請求額変更のみの場合のフォームスキーマ */
export const ToursChargeCustomerChargesFormValueSchema = object({
  customerCharge: ToursChargeCustomerChargeFormValueSchema
})

/** 請求額変更のみの場合のフォーム値 */
export type ToursChargeCustomerChargesFormValue = InferOutput<
  typeof ToursChargeCustomerChargesFormValueSchema
>
